import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import s from "./PaymentReceiptModal.module.css";
import { currentSchool } from "../../utils/currentSchool";

/**
 * Per-payment "Official Receipt" mini doc.
 *
 * Triggered from a payment row in either ChargePaymentsModal
 * (additional-charge receipts) or HistoryModal (tuition-payment
 * receipts). Renders a polished, print-ready receipt for one
 * transaction with two on-screen affordances:
 *
 *   - Print          → window.print(), scoped via @media print rules so
 *                      only the receipt body lands on the page (the
 *                      modal chrome and any other portals are hidden).
 *   - Download PDF   → html2canvas + jsPDF, A6 portrait so the doc is
 *                      a natural "small receipt" size when shared.
 *
 * Branding: school name + address mirror the SOA's hardcoded values
 * so the two documents read like siblings; if the SOA later moves
 * those into site-content, this file should follow.
 *
 * Security: every value is rendered through React's escaping; no
 * dangerouslySetInnerHTML, no external fetches, no iframe. PDF
 * download captures the caller's own DOM via html2canvas; no
 * third-party content boundary is crossed.
 *
 * Props:
 *   - payment  : the transaction row {id, amount, method,
 *                referenceNo, bankName, paidAt, recordedByUsername,
 *                recordedByRole, notes}.  Works for both
 *                AdditionalChargePayment and TuitionPayment shapes —
 *                they share these fields.
 *   - subject  : what was paid for: { label, sub? }.  `label` is the
 *                bold line ("Summer class", "Tuition fee"). `sub` is
 *                the smaller line beneath it ("Additional charge",
 *                "1st Semester · SY 2026-2027"). If omitted falls
 *                back to "Additional charge" so the existing
 *                additional-charge call sites stay backward-compatible.
 *   - student  : {firstName, lastName, studentNumber}
 *   - schoolYearLabel: optional SY label string ("2026-2027")
 *   - coverage : optional [{label, amount}]. When present, renders
 *                the "Coverage" breakdown so the parent sees what a
 *                multi-month tuition payment was applied to (e.g.
 *                "Nov ₱2,111 + Dec ₱2,111 + ..."). Omit for
 *                additional-charge receipts.
 *   - onClose  : dismiss handler
 */

// ── School branding (multi-tenant) — pulls from the active school
// selected in the topbar. A chain admin in school B issuing a receipt
// gets school B's letterhead, not the chain default. Branch users
// are locked to their one school, so this is always correct for them.
// Resolved fresh on every render so a topbar context switch is
// reflected immediately without a hard reload.
function currentSchoolBranding() {
  const s = currentSchool();
  return {
    name:    s.name || "Northpoint Center for Learning and Advancement",
    // The school entity doesn't carry a separate "address" column on
    // every branch yet (Phase 3.4 leaves address optional). When the
    // active school has no address on file, fall back to the short
    // name so the second letterhead line never reads "undefined."
    address: (typeof s.address === "string" && s.address.trim())
              || s.shortName || "",
  };
}

function fmtMoney(n) {
  return Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "long", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

/** Format a "YYYY-MM-DD" date string as "May 9, 2026". Falls back to
 *  the raw string if parsing fails so a bad value still renders
 *  something useful instead of "Invalid Date". */
function fmtDateLong(yyyyMmDd) {
  if (!yyyyMmDd) return "—";
  // Anchor at noon so timezone offsets can't flip the day backwards
  // for users east of UTC (e.g. PH +08).
  const d = new Date(yyyyMmDd + "T12:00:00");
  if (Number.isNaN(d.getTime())) return String(yyyyMmDd);
  return d.toLocaleDateString(undefined, {
    year: "numeric", month: "long", day: "numeric",
  });
}

/** Resolve the "actual receipt date" with a fallback to paidAt's date
 *  portion. Legacy rows have a null paymentReceivedOn — we still want
 *  the prominent block to show something sensible there, so the date
 *  portion of paidAt is the conservative fallback. */
function resolveReceivedOn(payment) {
  if (payment?.paymentReceivedOn) return payment.paymentReceivedOn;
  if (payment?.paidAt) {
    const d = new Date(payment.paidAt);
    if (!Number.isNaN(d.getTime())) {
      const y  = d.getFullYear();
      const m  = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    }
  }
  return null;
}

/** Build a stable display receipt # from the payment id —
 *  RCT-{paddedId} so it sorts naturally and humans can read it. */
function receiptNumber(p) {
  const id = p?.id;
  if (id == null) return "—";
  return `RCT-${String(id).padStart(6, "0")}`;
}

/** Cosmetic "amount in words" line for the receipt — keeps the
 *  layout balanced even when the digit value is short. Falls back
 *  silently if the value can't be parsed. */
function amountInWords(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "";
  const peso = Math.floor(v);
  const cents = Math.round((v - peso) * 100);
  const pesoLabel  = peso  === 1 ? "peso"   : "pesos";
  const centsLabel = cents === 1 ? "centavo" : "centavos";
  const pesoStr  = peso.toLocaleString();
  if (cents === 0) return `(${pesoStr} ${pesoLabel} only)`;
  return `(${pesoStr} ${pesoLabel} and ${cents} ${centsLabel} only)`;
}

export default function PaymentReceiptModal({
  payment,
  subject,
  // Legacy alias — earlier additional-charge call sites pass `charge`.
  // Treated as { label } if provided and `subject` isn't.
  charge,
  student,
  schoolYearLabel,
  coverage,
  onClose,
}) {
  // Normalise the prop shape so the rest of the component reads from
  // a single `effectiveSubject`. `subject` wins; otherwise we wrap
  // the legacy `charge` and assume "Additional charge" as the sub.
  const effectiveSubject = subject
    ? subject
    : (charge ? { label: charge.label, sub: "Additional charge" } : null);
  const receiptRef = useRef(null);
  const [pdfBusy, setPdfBusy]   = useState(false);
  const [error, setError]       = useState("");

  // Esc to close + body scroll lock while open. Same hooks as the
  // other modals in this feature so the keyboard behaviour is
  // consistent.
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        // Don't dismiss while a PDF is being generated — the canvas
        // capture is mid-flight and tearing down the DOM under it
        // would throw a confusing error in the console.
        if (pdfBusy) return;
        e.preventDefault();
        onClose?.();
      }
    }
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, pdfBusy]);

  // Belt-and-braces cleanup: if the user closes the modal mid-print
  // (Esc, click-out, etc.) make sure the data-printing flag on body
  // doesn't get left behind, which would suppress every other UI on
  // the page. We can't subscribe to `afterprint` from here cleanly
  // because handlePrint is synchronous and pops a native dialog, so
  // we reset on unmount as a safety net.
  useEffect(() => {
    return () => {
      if (document.body.getAttribute("data-printing") === "receipt") {
        document.body.removeAttribute("data-printing");
      }
    };
  }, []);

  if (!payment || !effectiveSubject) return null;

  const studentName = student
    ? `${student.lastName || ""}, ${student.firstName || ""}`.replace(/^, |, $/g, "")
    : "—";

  const methodChipClass =
      payment.method === "CASH"  ? s.methodChipCASH
    : payment.method === "BANK"  ? s.methodChipBANK
    : payment.method === "GCASH" ? s.methodChipGCASH
    : payment.method === "CARD"  ? s.methodChipCARD
    :                              s.methodChipOTHER;

  /** Print path — toggle the body attribute so @media print rules
   *  scope the printable area down to the receipt, fire native print,
   *  and clear the flag synchronously after the dialog returns.
   *
   *  Safety: also wired to window.onafterprint as a fallback for
   *  browsers that fire that asynchronously (Firefox returns from
   *  window.print synchronously, Chrome/Safari fire afterprint). */
  function handlePrint() {
    if (pdfBusy) return;
    setError("");
    try {
      document.body.setAttribute("data-printing", "receipt");
      const cleanup = () => {
        document.body.removeAttribute("data-printing");
        window.removeEventListener("afterprint", cleanup);
      };
      window.addEventListener("afterprint", cleanup);
      window.print();
      // Synchronous browsers: also clear immediately. The afterprint
      // listener above handles the async case if the engine queued
      // the dialog and returned first.
      document.body.removeAttribute("data-printing");
    } catch (e) {
      document.body.removeAttribute("data-printing");
      setError(`Print failed: ${e.message || e}`);
    }
  }

  /** PDF download — html2canvas the receipt subtree, then jsPDF a
   *  CUSTOM-SIZED page that exactly fits the receipt + a small
   *  margin. This guarantees a single-page PDF regardless of how
   *  many coverage rows or how long the notes are, and avoids
   *  awkward auto-paginating (which split a 4-month coverage
   *  receipt across two A6 pages in the previous version).
   *
   *  Why custom-fit instead of A4/A5: receipts are by nature
   *  variable-height; a fixed paper size either wastes vertical
   *  space (A4 with a small receipt at top) or paginates badly
   *  (A6 with a long coverage list). Real-world receipts (BIR
   *  ORs, thermal-printer tape) are custom-sized. PDF viewers
   *  handle "fit to page" gracefully, so users can still print on
   *  whatever paper they have.
   *
   *  Animation safety: html2canvas captures the synchronous DOM
   *  state, so any in-flight CSS animation can leak into the PNG
   *  (e.g. an `opacity: 0` keyframe during a delay window would
   *  invisible-out the amount value — exactly what happened in
   *  the bug screenshot). We toggle a `printing` data-attribute
   *  on the receipt element which a `*` selector in the CSS
   *  module pauses every animation/transition and forces opacity
   *  to 1 — guarantees the captured image is the resting state. */
  async function handleDownloadPDF() {
    if (pdfBusy) return;
    if (!receiptRef.current) return;
    setError("");
    setPdfBusy(true);
    // Freeze animations on the receipt subtree before capture so
    // the canvas always grabs the resting visual state.
    receiptRef.current.setAttribute("data-capture", "true");
    // Force a layout flush so the browser commits the new state
    // before html2canvas reads it.
    void receiptRef.current.offsetHeight;
    try {
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      // Convert the captured canvas (which is at 2× scale of the
      // CSS pixels) back to mm at the standard 96-CSS-DPI base so
      // the PDF page is exactly the size of the receipt as the
      // user saw it on screen.
      const SCALE = 2;
      const PX_TO_MM = 25.4 / 96 / SCALE;
      const contentWmm = canvas.width  * PX_TO_MM;
      const contentHmm = canvas.height * PX_TO_MM;

      const margin = 8;
      const pageW = contentWmm + margin * 2;
      const pageH = contentHmm + margin * 2;

      const pdf = new jsPDF({
        orientation: pageH >= pageW ? "portrait" : "landscape",
        unit: "mm",
        format: [pageW, pageH],
      });

      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", margin, margin, contentWmm, contentHmm);

      const safe = (str) => (str || "").replace(/[\\/:*?"<>|]+/g, "").trim();
      const filename = `Receipt - ${safe(receiptNumber(payment))} - ${safe(studentName)}.pdf`;
      pdf.save(filename);
    } catch (e) {
      setError(`PDF failed: ${e.message || e}`);
    } finally {
      // Always release the freeze, even on error, so the on-screen
      // view doesn't stay frozen if something throws mid-capture.
      receiptRef.current?.removeAttribute("data-capture");
      setPdfBusy(false);
    }
  }

  return createPortal(
    <div className={s.overlay}
         data-print-root="receipt">
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.head}>
          <h3 className={s.title}>
            <span aria-hidden>🧾</span>
            Payment receipt
          </h3>
          <button className={s.close}
                  type="button"
                  onClick={onClose}
                  disabled={pdfBusy}
                  aria-label="Close">
            ×
          </button>
        </div>

        {/* Inner scroll surface — the head (title) and footer (action
            buttons) stay pinned outside this wrapper so Print /
            Download remain reachable even on a long receipt. */}
        <div className={s.body}>
        {error && <div className={s.errMsg}>{error}</div>}

        <div className={s.receiptWrap}>
          <div className={s.receipt} ref={receiptRef}>
            <div className={s.headerBand}>
              <div className={s.schoolName}>{currentSchoolBranding().name}</div>
              <div className={s.schoolAddr}>{currentSchoolBranding().address}</div>
              <div className={s.docLabel}>Official Receipt</div>
            </div>

            <div className={s.metaGrid}>
              <span className={s.metaLabel}>RCT&nbsp;No.</span>
              <span className={s.metaValue}>{receiptNumber(payment)}</span>

              <span className={s.metaLabel}>OR&nbsp;#</span>
              <span className={s.metaValue}>{payment.orNumber || "—"}</span>

              <span className={s.metaLabel}>Ref&nbsp;#</span>
              <span className={s.metaValue}>{payment.referenceNo || "—"}</span>
            </div>

            {/* Date received — the receipt's hero date. Prominent so
                anyone reading the printed receipt sees the *real*
                receipt day at a glance, even when the record was
                entered into the system on a different (later) date. */}
            <div className={s.receivedBlock}>
              <div className={s.receivedLabel}>Date received</div>
              <div className={s.receivedValue}>
                {fmtDateLong(resolveReceivedOn(payment))}
              </div>
            </div>

            <div className={s.section}>
              <div className={s.sectionLabel}>Received from</div>
              <div className={s.sectionValue}>{studentName}</div>
              <div className={s.sectionSub}>
                {student?.studentNumber && <span>#{student.studentNumber}</span>}
                {student?.studentNumber && schoolYearLabel && <span> · </span>}
                {schoolYearLabel && <span>SY {schoolYearLabel}</span>}
              </div>
            </div>

            <div className={s.section}>
              <div className={s.sectionLabel}>In payment for</div>
              <div className={s.sectionValue}>{effectiveSubject.label || "—"}</div>
              {effectiveSubject.sub && (
                <div className={s.sectionSub}>{effectiveSubject.sub}</div>
              )}
            </div>

            {/* Coverage breakdown — only renders for tuition payments
                where one transaction was applied to multiple slots
                (e.g. ₱7,777 covering Nov + Dec + Jan + Feb). The
                parent reads it as "yes, my last payment landed on
                exactly these months in exactly these amounts". */}
            {Array.isArray(coverage) && coverage.length > 0 && (
              <div className={s.section}>
                <div className={s.sectionLabel}>Coverage</div>
                <ul className={s.coverList}>
                  {coverage.map((c, i) => (
                    <li key={i} className={s.coverRow}>
                      <span className={s.coverLabel}>{c.label}</span>
                      <span className={s.coverAmount}>
                        ₱{fmtMoney(c.amount)}
                        {c.overpay && (
                          <span className={s.coverOverpay}> · overpay</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={s.amountBlock}>
              <div className={s.amountLabel}>Amount paid</div>
              <div className={s.amountValue}>₱{fmtMoney(payment.amount)}</div>
              <div className={s.amountInWords}>{amountInWords(payment.amount)}</div>
            </div>

            <div className={s.payMetaRow}>
              <span className={s.metaLabel}>Method</span>
              <span>
                <span className={`${s.methodChip} ${methodChipClass}`}>
                  {payment.method || "—"}
                </span>
                {payment.method === "BANK" && payment.bankName && (
                  <span className={s.bankName}>{payment.bankName}</span>
                )}
              </span>

              {payment.notes && (
                <>
                  <span className={s.metaLabel}>Notes</span>
                  <span className={s.noteBox}>{payment.notes}</span>
                </>
              )}
            </div>

            <div className={s.footer}>
              <div className={s.recordedBy}>
                Recorded by:{" "}
                <span className={s.recordedByName}>
                  {payment.recordedByUsername || "—"}
                </span>
                {payment.recordedByRole && (
                  <span> · {payment.recordedByRole}</span>
                )}
              </div>

              {/* System-entry timestamp — deliberately small + muted so
                  the prominent "Date received" above stays the headline
                  date. Audit-only: when the record was keyed in. */}
              <div className={s.recordedAt}>
                Entered into system: {fmtDateTime(payment.paidAt)}
              </div>

              <div className={s.signatureBlock}>
                <div className={s.signatureLine}>
                  Authorized signature
                </div>
              </div>

              <div className={s.proofNote}>
                This document serves as proof of payment.
                Please keep it for your records.
              </div>
            </div>
          </div>
        </div>
        </div>{/* /.body */}

        <div className={s.actions}>
          <button type="button"
                  className={s.btnGhost}
                  onClick={onClose}
                  disabled={pdfBusy}>
            Close
          </button>
          <button type="button"
                  className={s.btnSecondary}
                  onClick={handleDownloadPDF}
                  disabled={pdfBusy}
                  title="Save this receipt as a PDF">
            {pdfBusy ? "Generating…" : "⬇ Download PDF"}
          </button>
          <button type="button"
                  className={s.btnPrimary}
                  onClick={handlePrint}
                  disabled={pdfBusy}
                  title="Print this receipt">
            🖨 Print
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
